'use client';
import styled, { css, keyframes } from 'styled-components';
import { variants } from './variants';

// Resolves a variant color value: theme token (contains '.'), raw CSS value, or null → transparent
const resolveColor = (value, theme) => {
  if (!value) return 'transparent';
  if (value.includes('.')) return theme.colors.get(value);
  return value;
};

const sheenSlide = keyframes`
  from { transform: translateX(-120%) skewX(-15deg); }
  to   { transform: translateX(220%) skewX(-15deg); }
`;

export const StyledButton = styled.button`
  ${props => props.theme.typography.button}
  border-radius: 500px;
  padding: ${props =>
    props.theme.spacing(props.$verticalPadding, props.$horizontalPadding)};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: ${props => {
    const isFullWidth = props.$fullWidth || props.$fullWidthMobile;
    if (isFullWidth) return '0';
    return props.$minWidth ? `${props.$minWidth}px` : '44px';
  }};
  min-height: 44px;
  width: ${props =>
    props.$fullWidth || props.$fullWidthMobile ? '100%' : 'auto'};
  position: relative;
  transition: ${props => {
    const v = variants[props.$variant];
    return v?.transition
      ? `${v.transitionProperties} ${v.transition}`
      : 'color, border-color, background-color 200ms ease';
  }};
  white-space: nowrap;
  ${props => {
    const variantHeight = variants[props.$variant]?.height;
    const height = variantHeight ?? props.$height;
    return height && `height: ${height};`;
  }}

  ${({ $variant, theme }) => {
    const v = variants[$variant];
    return css`
      background-color: ${resolveColor(v.active.background, theme)};
      border: ${v.borderWidth ?? '1px'} solid
        ${resolveColor(v.active.border, theme)};
      color: ${resolveColor(v.active.text, theme)};
      ${v.extraStyles ?? ''}

      //TODO: These pseudo-classes don't have designs yet and will be updated
      //on WEB-1543

      &:hover {
        ${v.hover
          ? `
                background-color: ${resolveColor(v.hover.background, theme)};
                border-color: ${resolveColor(v.hover.border, theme)};
                color: ${resolveColor(v.hover.text, theme)};
              `
          : `
                border-color: ${
                  $variant === 'cancel'
                    ? theme.colors.status.errorPrimary
                    : $variant !== 'primary'
                    ? theme.colors.border.dark
                    : theme.colors.hero.lilac
                };
              `}
        outline: none;
      }

      &:active {
        ${v.hover
          ? `
                background-color: ${resolveColor(v.hover.background, theme)};
                border-color: ${resolveColor(v.hover.border, theme)};
                color: ${resolveColor(v.hover.text, theme)};
              `
          : `border-color: ${theme.colors.text.primary};`}
      }

      &:disabled {
        opacity: 0.5;
      }

      &:focus-visible {
        outline: 2px solid
          ${v.focusRingColor
            ? resolveColor(v.focusRingColor, theme)
            : 'currentColor'};
        outline-offset: 2px;
      }

      ${v.sheen
        ? css`
            &::before {
              content: '';
              position: absolute;
              top: 0;
              left: 0;
              width: 50%;
              height: 100%;
              background: linear-gradient(
                120deg,
                transparent 0%,
                rgba(255, 255, 255, 0.35) 50%,
                transparent 100%
              );
              transform: translateX(-120%) skewX(-15deg);
              pointer-events: none;
            }
            &:hover::before {
              animation: ${sheenSlide} 1000ms ease forwards;
            }
          `
        : ''}
    `;
  }}

  ${props =>
    props.$hasHoverIcon &&
    css`
      border: 0;
      &:hover {
        transform: translateY(-2px);

        & ${HoverIcon} {
          opacity: 1;
          transform: translateX(0) translateY(-50%);
        }
      }
    `}


  ${props =>
    props.$percentWidthMobile &&
    !props.$fullWidth &&
    !props.$fullWidthMobile &&
    css`
      padding-left: 0;
      padding-right: 0;
      width: ${props.$percentWidthMobile}%;
    `}

    ${props => props.$icon === 'end' && WithPlusIcon};

  @media ${props => props.theme.tablet} {
    min-width: ${props => {
      const isFullWidth = props.$fullWidth || props.$fullWidthDesktop;
      if (isFullWidth) return '0';
      return props.$minWidth ? `${props.$minWidth}px` : '44px';
    }};
    width: ${props =>
      props.$fullWidth || props.$fullWidthDesktop ? '100%' : 'auto'};
    padding: ${props =>
      props.theme.spacing(props.$verticalPadding, props.$horizontalPadding)};
  }
`;

export const ButtonLoaderContainer = styled.div`
  position: absolute;
  right: ${props => props.theme.spacing(4)};
`;

export const IconContainer = styled.span`
  display: inline-flex;
  padding-right: ${props =>
    props.position === 'before' && props.theme.spacing(2)};
  padding-left: ${props =>
    props.position === 'after' && props.theme.spacing(2)};
`;

export const HoverIcon = styled.div`
  line-height: 0;
  position: absolute;
  right: ${props => props.theme.spacing(5)};
  top: 50%;
  transform: translateY(-50%);
  transition: opacity 0.5s, transform 0.5s;

  @media ${props => props.theme.tablet} {
    opacity: 0;
    transform: translateX(-4px) translateY(-50%);
  }
`;

const WithPlusIcon = css`
  background: #f1f4f8;

  &:hover {
    border-color: ${props => props.theme.colors.hero.plum};
    outline: none;
  }

  svg {
    position: absolute;
    left: 89.13%;
    right: 10.67%;
    top: 26.28%;
    bottom: 22.78%;
  }
`;
